import { useState, useEffect, useRef } from 'react';
import { CatalogEntry, addCountryFromCatalog, AdminLocationInfo } from '../api/adminApi';
import styles from './AddCountryModal.module.css';
import { flagEmoji } from '../utils/flag';

const METHOD_LABELS: Record<string, string> = {
  MOON_SIGHTING: 'Sighting',
  CALCULATION: 'Calculation',
  BOTH: 'Both',
};

interface Props {
  catalog: CatalogEntry[];
  onClose: () => void;
  onAdded: (location: AdminLocationInfo) => void;
}

export default function AddCountryModal({ catalog, onClose, onAdded }: Props) {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = catalog.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.code.toLowerCase().includes(query.toLowerCase()) ||
    c.region.toLowerCase().includes(query.toLowerCase())
  );

  // Group by region
  const byRegion = filtered.reduce<Record<string, CatalogEntry[]>>((acc, c) => {
    (acc[c.region] = acc[c.region] || []).push(c);
    return acc;
  }, {});

  async function handleAdd(code: string) {
    setAdding(code);
    setError('');
    try {
      const result = await addCountryFromCatalog(code);
      onAdded(result.location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add country');
    } finally {
      setAdding(null);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.title}>Add Country</h2>
            <p className={styles.subtitle}>{catalog.length} countries available</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.searchWrap}>
          <input
            ref={inputRef}
            className={styles.searchInput}
            type="text"
            placeholder="Search by country name, code, or region…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.listWrap}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>No countries match your search.</div>
          ) : (
            Object.entries(byRegion).map(([region, countries]) => (
              <div key={region} className={styles.regionGroup}>
                <div className={styles.regionLabel}>{region}</div>
                {countries.map(c => (
                  <div key={c.code} className={styles.countryRow}>
                    <div className={styles.countryLeft}>
                      <span className={styles.flag}>{flagEmoji(c.code)}</span>
                      <div>
                        <span className={styles.countryName}>{c.name}</span>
                        <span className={styles.countryMeta}>{c.timezone} · {METHOD_LABELS[c.defaultMethod]}</span>
                      </div>
                    </div>
                    <button
                      className={styles.addBtn}
                      onClick={() => handleAdd(c.code)}
                      disabled={adding === c.code}
                    >
                      {adding === c.code ? '…' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
